import type { Clock, ClockState, Instant, Micros, TimerHandle } from "./types.js";
import { CascadeBudgetError } from "./types.js";
import { TimerHeap } from "./heap.js";
import { hostTimeout, type HostTimer } from "./host-timer.js";
import { TidMinter } from "./tid.js";

/** spanReplay firing limit per jump (ADR-13). Same value as Go/Rust siblings. */
export const DEFAULT_CASCADE_BUDGET = 10_000;

/** Maximum clock rate (ADR-13: rate ∈ [0, 60]). */
export const MAX_RATE = 60;

interface SimOpts {
  /** Local monotonic µs source. Default: performance-based real source. */
  monotonic?: () => number;
  /** 10-bit clock id for TID minting. Sim runs inject this for determinism. */
  clkid: number;
  /** spanReplay firing limit per jump (ADR-13: default 10k). */
  cascadeBudget?: number;
}

interface Entry { cb: () => void; cancelled: boolean; }

/**
 * SimClock follower (ADR-13): extrapolates from broadcast clockState; now() is a
 * local computation. Jump = applySpanReplay: drains due timers in scheduled order,
 * each callback observing its own scheduled instant.
 */
export class SimClockFollower implements Clock {
  private state: ClockState;
  private lastNow = 0;
  private heap = new TimerHeap<Entry>();
  private seq = 0;
  private readonly mono: () => number;
  private readonly budget: number;
  private readonly minter: TidMinter;
  /** During spanReplay drain, now() reports the firing timer's scheduled instant. */
  private drainNow: Instant | null = null;
  private wake: HostTimer | null = null;

  constructor(opts: SimOpts) {
    this.mono = opts.monotonic ?? defaultMonotonic();
    this.budget = opts.cascadeBudget ?? DEFAULT_CASCADE_BUDGET;
    this.minter = new TidMinter(opts.clkid);
    this.state = { anchorSim: 0, anchorMono: this.mono(), rate: 0, generation: 0 };
  }

  now(): Instant {
    if (this.drainNow !== null) return this.drainNow;
    const { anchorSim, anchorMono, rate } = this.state;
    const computed = anchorSim + Math.trunc((this.mono() - anchorMono) * rate);
    this.lastNow = Math.max(this.lastNow, computed);
    return this.lastNow;
  }

  tid(): string { return this.minter.mint(this.now()); }

  // Constraint: state applied during a spanReplay drain is overwritten by the
  // drain's closing settle(); simd never publishes clockState mid-drain.
  applyClockState(s: ClockState): void {
    if (s.generation <= this.state.generation) return; // stale (ADR-13)
    if (s.rate < 0 || s.rate > MAX_RATE) throw new RangeError(`rate out of range: ${s.rate}`);
    this.state = { ...s };
    this.rescheduleWake();
  }

  at(t: Instant, cb: () => void): TimerHandle {
    const entry: Entry = { cb, cancelled: false };
    this.heap.push({ at: t, seq: this.seq++, value: entry });
    this.rescheduleWake();
    return { cancel: () => { entry.cancelled = true; } };
  }

  after(d: Micros, cb: () => void): TimerHandle { return this.at(this.now() + d, cb); }

  pendingCount(): number {
    let n = 0;
    for (const e of this.heap.entries()) if (!e.value.cancelled) n++;
    return n;
  }

  /**
   * Jump (ADR-13): drain timers scheduled in (now, to] in order; each callback runs
   * with now() pinned to its scheduled instant; anything it schedules inside the span
   * joins the drain. Budget exhaustion throws, leaving the clock at the failed instant.
   */
  applySpanReplay(_from: Instant, to: Instant): void {
    // Constraint: a drain callback must not start another drain — nested replays
    // would corrupt drainNow pinning and the closing settle().
    if (this.drainNow !== null) throw new Error("applySpanReplay is not re-entrant (called from within a drain callback)");
    const start = this.now();
    if (to <= start) throw new RangeError(`spanReplay must move forward: now=${start} to=${to}`);
    let fired = 0;
    try {
      for (;;) {
        const head = this.heap.peek();
        if (!head || head.at > to) break;
        this.heap.pop();
        if (head.value.cancelled) continue;
        if (fired >= this.budget) {
          // ADR-13: clock rests at the DENIED timer's instant; the timer that
          // exceeded the budget has NOT fired — re-push it.
          this.settle(head.at);
          this.heap.push(head);
          throw new CascadeBudgetError(fired, head.at);
        }
        this.drainNow = Math.max(head.at, this.lastNow);
        this.lastNow = this.drainNow;
        fired++;
        head.value.cb();
      }
      this.settle(to);
    } finally {
      this.drainNow = null;
      this.rescheduleWake();
    }
  }

  /** Resume normal extrapolation anchored at simInstant. */
  private settle(simInstant: Instant): void {
    this.lastNow = Math.max(this.lastNow, simInstant);
    this.state = { ...this.state, anchorSim: this.lastNow, anchorMono: this.mono() };
  }

  /** Host wakeup so timers fire while rate > 0 (live demo mode, no jump). */
  private rescheduleWake(): void {
    if (this.wake !== null) { this.wake.clear(); this.wake = null; }
    if (this.drainNow !== null || this.state.rate <= 0) return;
    const head = this.heap.peek();
    if (!head) return;
    const delayMs = Math.max(0, (head.at - this.now()) / this.state.rate / 1000);
    this.wake = hostTimeout(() => { this.wake = null; this.fireDue(); }, delayMs); // chained past Node's 2^31-1 ms setTimeout clamp
  }

  private fireDue(): void {
    try {
      for (;;) {
        const head = this.heap.peek();
        if (!head || head.at > this.now()) break;
        this.heap.pop();
        if (head.value.cancelled) continue;
        head.value.cb();
      }
    } finally {
      // A throwing callback must not stall the wake chain.
      this.rescheduleWake();
    }
  }
}

function defaultMonotonic(): () => number {
  const origin = performance.now();    // wallclock-allow: the one sanctioned monotonic read (ADR-13: "inside the SimClock extrapolator")
  return () => Math.round((performance.now() - origin) * 1000);    // wallclock-allow: monotonic extrapolator read
}
